import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface RevealCountdownProps {
    game: {
        id: string;
        name: string;
        revealSchedule: {
            enabled?: boolean;
            day: string;
            time: string;
            durationHours: number;
        };
    };
    timezone: string;
}

/**
 * Countdown timer shown on dashboard 24 hours before the next reveal
 */
export default function RevealCountdown({ game, timezone }: RevealCountdownProps) {
    const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number } | null>(null);
    const [isRevealActive, setIsRevealActive] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            const result = calculateTimeToReveal(game.revealSchedule, timezone);

            if (result.isActive) {
                setIsRevealActive(true);
                setTimeLeft(null);
            } else if (result.hoursLeft <= 24) {
                setIsRevealActive(false);
                setTimeLeft({
                    hours: result.hoursLeft,
                    minutes: result.minutesLeft,
                    seconds: result.secondsLeft,
                });
            } else {
                setTimeLeft(null);
                setIsRevealActive(false);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [game.revealSchedule, timezone]);

    // Don't show if reveal schedule is disabled
    if (!game.revealSchedule?.enabled) {
        return null;
    }

    // Don't show if more than 24 hours away
    if (!timeLeft && !isRevealActive) {
        return null;
    }

    // Reveal is active - show "Live Now" card
    if (isRevealActive) {
        return (
            <Link
                to={`/games/${game.id}/reveal`}
                className="block bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition transform hover:scale-[1.02]"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="text-4xl animate-pulse">👀</div>
                        <div>
                            <div className="text-white font-bold text-lg">
                                Intermittent Peek is LIVE!
                            </div>
                            <div className="text-purple-100 text-sm">
                                See how the top participants are doing in {game.name}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-white">
                        <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                        </span>
                        <span className="font-semibold">LIVE NOW</span>
                    </div>
                </div>
            </Link>
        );
    }

    // Countdown mode - NOT clickable (reveal hasn't started yet)
    return (
        <div
            className="block bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl shadow-lg p-6 mb-6"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="text-4xl">⏰</div>
                    <div>
                        <div className="text-white font-bold text-lg">
                            Next Reveal Coming Soon!
                        </div>
                        <div className="text-indigo-100 text-sm">
                            Peek at the leaderboard for {game.name}
                        </div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="flex gap-2 text-white font-mono">
                        <TimeBlock value={timeLeft!.hours} label="hrs" />
                        <span className="text-2xl font-bold">:</span>
                        <TimeBlock value={timeLeft!.minutes} label="min" />
                        <span className="text-2xl font-bold">:</span>
                        <TimeBlock value={timeLeft!.seconds} label="sec" />
                    </div>
                </div>
            </div>
        </div>
    );
}

function TimeBlock({ value, label }: { value: number; label: string }) {
    return (
        <div className="text-center">
            <div className="bg-white/20 rounded-lg px-3 py-1 min-w-[50px]">
                <span className="text-2xl font-bold">{value.toString().padStart(2, '0')}</span>
            </div>
            <div className="text-xs text-indigo-200 mt-1">{label}</div>
        </div>
    );
}

function calculateTimeToReveal(
    schedule: { day: string; time: string; durationHours: number },
    timezone: string
): { hoursLeft: number; minutesLeft: number; secondsLeft: number; isActive: boolean } {
    const now = new Date();

    // Get current time in the specified timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'long',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const currentDay = parts.find(p => p.type === 'weekday')?.value?.toLowerCase() || '';
    const currentHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
    const currentMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
    const currentSecond = parseInt(parts.find(p => p.type === 'second')?.value || '0');

    const [scheduleHour, scheduleMinute] = schedule.time.split(':').map(Number);
    const scheduleDay = schedule.day.toLowerCase();

    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayIndex = days.indexOf(currentDay);
    const scheduleDayIndex = days.indexOf(scheduleDay);

    if (currentDayIndex === -1 || scheduleDayIndex === -1) {
        return { hoursLeft: 999, minutesLeft: 0, secondsLeft: 0, isActive: false };
    }

    // Calculate days until reveal
    let daysUntil = scheduleDayIndex - currentDayIndex;
    if (daysUntil < 0) daysUntil += 7;
    if (daysUntil === 0) {
        // Same day - check if we've passed the time
        const currentTotalSeconds = currentHour * 3600 + currentMinute * 60 + currentSecond;
        const scheduleTotalSeconds = scheduleHour * 3600 + (scheduleMinute || 0) * 60;
        const endTotalSeconds = scheduleTotalSeconds + schedule.durationHours * 3600;

        if (currentTotalSeconds >= scheduleTotalSeconds && currentTotalSeconds < endTotalSeconds) {
            // Reveal is active!
            return { hoursLeft: 0, minutesLeft: 0, secondsLeft: 0, isActive: true };
        } else if (currentTotalSeconds >= endTotalSeconds) {
            // Reveal has ended, calculate next week
            daysUntil = 7;
        }
    }

    // Calculate total seconds until reveal
    const hoursUntilDay = daysUntil * 24;
    const hoursDiff = scheduleHour - currentHour;
    const minutesDiff = (scheduleMinute || 0) - currentMinute;
    const secondsDiff = 0 - currentSecond;

    let totalSeconds = hoursUntilDay * 3600 + hoursDiff * 3600 + minutesDiff * 60 + secondsDiff;
    if (totalSeconds < 0) totalSeconds += 7 * 24 * 3600;

    const hoursLeft = Math.floor(totalSeconds / 3600);
    const minutesLeft = Math.floor((totalSeconds % 3600) / 60);
    const secondsLeft = totalSeconds % 60;

    return { hoursLeft, minutesLeft, secondsLeft, isActive: false };
}
